'use client';

import React, { createRef, FC, useEffect } from 'react';
import { IToastProps, Toast as UiKitToast } from '@mofid-securities/uikit';
import { withNoSSR } from '@/components/NoSSR';
import {
  getRefMethods,
  TOverlayRef,
  useOverlayImperativeHandle,
} from '@/hooks/overlayImperativeHandle';
import { classnames } from '@/utils/classnames';

export interface IToastParam extends Omit<IToastProps, 'isOpen' | 'onClose'> {
  duration?: number;
}

const defaultParam: IToastParam = {
  children: null,
  hasIcon: true,
  isDismissible: true,
  hasShadow: true,
  offset: 95,
  duration: 4 * 1000,
};

const ref = createRef<TOverlayRef<IToastParam>>();

let timeoutId: number = 0;

export const ToastContainer: FC = withNoSSR(() => {
  const { param, close } = useOverlayImperativeHandle<IToastParam>(
    ref,
    defaultParam
  );

  useEffect(() => {
    clearTimeout(timeoutId);
    if (param.isOpen && param?.duration) {
      timeoutId = window.setTimeout(close, param?.duration);
    }
  }, [param.isOpen, param?.duration, param._key]);

  return !!param.children ? (
    <div className="text-center">
      <UiKitToast
        {...param}
        alertProps={{
          className: `py-3 ${param?.alertProps?.className || ''} ${
            param?.isDismissible ? '' : 'children:w-full'
          }`,
        }}
        // Bottomsheet has a higher z-index than our toasts,
        // so we need this adjustment to ensure the toast appears above it.
        className={classnames('!z-[999999]', param?.className)}
        isOpen={param.isOpen}
        onClose={param?.isDismissible ? close : undefined}
      >
        {param.children}
      </UiKitToast>
    </div>
  ) : null;
});

const Toast = getRefMethods<IToastParam>(ref);
export default Toast;
